import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, Flame, Mic, Headphones, Instagram, Facebook, Twitch, Youtube, Music2, Radio, Play } from "lucide-react";
import { fetchArticles, fetchStats, fetchShow } from "../lib/api";
import { LOGO_PRIMARY, STUDIO_SHOT, SOCIALS, formatNumber } from "../lib/brand";
import ArticleCard from "../components/ArticleCard";
import NewsletterForm from "../components/NewsletterForm";
import SEO from "../components/SEO";

const ICONS = { Instagram, Facebook, Twitch, Youtube, Music2, Radio };

export default function Home() {
  const [articles, setArticles] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [stats, setStats] = useState({});
  const [show, setShow] = useState(null);

  useEffect(() => {
    fetchArticles({ limit: 8 }).then((r) => setArticles(r.articles || [])).catch(() => {});
    fetchArticles({ featured: true, limit: 3 }).then((r) => setFeatured(r.articles || [])).catch(() => {});
    fetchStats().then(setStats).catch(() => {});
    fetchShow().then(setShow).catch(() => {});
  }, []);

  const hero = featured[0] || articles[0];

  return (
    <div data-testid="home-page">
      <SEO
        title="Sports & Conversation"
        description="The sports podcast that refuses to play it safe. Heated debates, hot takes, and the gritty side of the game from two former players. Raw. Bold. Unapologetic."
        image={STUDIO_SHOT}
      />
      {/* HERO */}
      <section className="relative border-b-2 border-[var(--ink)]" data-testid="hero-section">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 pt-8 md:pt-12 pb-12">
          {/* Masthead row */}
          <div className="flex items-end justify-between pb-4 border-b-2 border-[var(--ink)] mb-8">
            <div>
              <div className="text-kicker">Vol. 1 · No. {Math.max(articles.length, 9)}</div>
              <div className="font-mono-ed text-xs mt-1 text-[var(--muted)]">
                {new Date().toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" })}
              </div>
            </div>
            <div className="hidden md:block font-mono-ed text-xs text-[var(--muted)] uppercase tracking-widest">
              The Sports Podcast Press Archive
            </div>
            <div className="text-right">
              <div className="text-kicker">Edition</div>
              <div className="font-display text-lg">Monday Drop</div>
            </div>
          </div>

          <div className="grid lg:grid-cols-12 gap-10 items-start">
            {/* Left: Hero headline */}
            <div className="lg:col-span-7 fade-up">
              <div className="flex items-center gap-3 mb-6">
                <span className="tape">Raw · Bold · Unapologetic</span>
                <span className="text-kicker hidden sm:inline">New Episode Mondays</span>
              </div>

              <h1 className="text-headline text-[var(--ink)] text-[clamp(2.8rem,8vw,6.5rem)] mb-4">
                The sports talk that<br />
                <span className="text-[var(--rust)]">refuses</span> to play<br />
                it safe.
              </h1>

              <p className="text-dek text-xl md:text-2xl max-w-xl mb-8">
                Two former players. Heated debates. Hot takes. The gritty side of the game — no PR filter, no network polish.
              </p>

              <div className="flex flex-wrap gap-4">
                <Link to="/listen" className="btn-rust" data-testid="hero-listen-cta">
                  <Play size={16} fill="currentColor" /> Listen Now
                </Link>
                <Link to="/editorial" className="btn-ghost" data-testid="hero-editorial-cta">
                  Read the Recaps <ArrowRight size={16} />
                </Link>
              </div>

              {/* Quick stats inline */}
              <div className="mt-10 grid grid-cols-3 gap-6 pt-8 border-t border-[var(--line)]">
                <div>
                  <div className="stat-number text-4xl md:text-5xl">{formatNumber(stats.avg_monthly_listeners)}</div>
                  <div className="text-kicker mt-1">Monthly Listeners</div>
                </div>
                <div>
                  <div className="stat-number text-4xl md:text-5xl text-[var(--rust)]">{formatNumber(stats.total_downloads)}</div>
                  <div className="text-kicker mt-1">Downloads</div>
                </div>
                <div>
                  <div className="stat-number text-4xl md:text-5xl">{show?.episodes || "40+"}</div>
                  <div className="text-kicker mt-1">Episodes Shipped</div>
                </div>
              </div>
            </div>

            {/* Right: Hero image / logo */}
            <div className="lg:col-span-5 fade-up-d2">
              <div className="relative frame-corners">
                <img src={STUDIO_SHOT}
                     alt="Unfiltered & On-Ball studio"
                     loading="eager"
                     className="w-full border-2 border-[var(--ink)] aspect-[4/5] object-cover" />
                <div className="absolute -bottom-4 -right-4 bg-[var(--rust)] text-[var(--paper)] px-4 py-3 border-2 border-[var(--ink)] shadow-[4px_4px_0_var(--ink)]">
                  <div className="font-mono-ed text-[0.65rem] uppercase tracking-widest opacity-80">On Air</div>
                  <div className="font-display text-2xl leading-none">LIVE</div>
                </div>
              </div>
              <div className="mt-6 flex items-center gap-3 flex-wrap">
                {SOCIALS.slice(0, 6).map((s) => {
                  const Ic = ICONS[s.icon] || Music2;
                  return (
                    <a key={s.key} href={s.url} target="_blank" rel="noreferrer"
                       className="flex items-center gap-2 border-2 border-[var(--ink)] px-3 py-2 text-xs font-display uppercase tracking-widest hover:bg-[var(--ink)] hover:text-[var(--paper)] transition"
                       data-testid={`hero-social-${s.key}`}>
                      <Ic size={14} /> {s.label}
                    </a>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FEATURED STRIP */}
      {featured.length > 0 && (
        <section className="border-b-2 border-[var(--ink)] py-14" data-testid="featured-section">
          <div className="max-w-[1400px] mx-auto px-5 md:px-8">
            <div className="flex items-center justify-between mb-8 pb-4 border-b-2 border-[var(--ink)]">
              <div>
                <div className="text-kicker flex items-center gap-2"><Flame size={14} /> Featured</div>
                <h2 className="text-headline text-4xl md:text-5xl">The Main Event</h2>
              </div>
              <Link to="/editorial" className="hidden sm:inline-flex nav-link items-center gap-1">
                All Stories <ArrowRight size={14} />
              </Link>
            </div>
            <div className="grid md:grid-cols-3 gap-10">
              {featured.slice(0, 3).map((a, i) => (
                <div key={a.slug} className={`fade-up-d${Math.min(i + 1, 3)}`}>
                  <ArticleCard article={a} size={i === 0 ? "lg" : "md"} />
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* LATEST RECAPS */}
      <section className="py-14 border-b-2 border-[var(--ink)]" data-testid="latest-section">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8">
          <div className="flex items-end justify-between mb-8 pb-4 border-b-2 border-[var(--ink)]">
            <div>
              <div className="text-kicker">Editorial Hub</div>
              <h2 className="text-headline text-4xl md:text-5xl">Latest Recaps</h2>
            </div>
            <Link to="/editorial" className="btn-ghost text-xs">Browse Archive</Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-10">
            {articles.slice(0, 4).map((a) => (
              <ArticleCard key={a.slug} article={a} size="sm" />
            ))}
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="bg-[var(--ink)] text-[var(--paper)] py-16 md:py-24 relative overflow-hidden noise-overlay" data-testid="newsletter-section">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8 relative">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="text-kicker text-[var(--rust)]">Don't let the algorithm win</div>
              <h2 className="text-headline text-5xl md:text-6xl mt-3">
                Get the drop.<br />
                <span className="text-[var(--rust)]">Straight to your inbox.</span>
              </h2>
              <p className="font-serif-ed text-lg md:text-xl text-[var(--paper-deep)] mt-5 max-w-lg">
                Monday-morning takes. Wednesday draft boards. Weekend recaps. No corporate, no filler — just the unfiltered drop.
              </p>
            </div>
            <div className="bg-[var(--paper)] text-[var(--ink)] p-7 md:p-9 border-2 border-[var(--rust)] shadow-[8px_8px_0_var(--rust)]">
              <div className="flex items-center gap-3 mb-5">
                <Mic size={18} className="text-[var(--rust)]" />
                <span className="font-display uppercase tracking-widest text-sm">Join the Huddle</span>
              </div>
              <NewsletterForm variant="stacked" source="home_newsletter" />
              <p className="text-xs text-[var(--muted)] mt-4 font-mono-ed uppercase tracking-widest">
                Unsubscribe anytime. We hate spam too.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* MEDIA KIT CTA */}
      <section className="py-16 border-b-2 border-[var(--ink)]" data-testid="media-kit-cta">
        <div className="max-w-[1400px] mx-auto px-5 md:px-8">
          <div className="grid md:grid-cols-5 gap-10 items-center">
            <div className="md:col-span-2">
              <img src={LOGO_PRIMARY} alt="Unfiltered & On-Ball logo"
                   loading="lazy"
                   className="w-full max-w-sm mx-auto" />
            </div>
            <div className="md:col-span-3">
              <div className="text-kicker flex items-center gap-2"><Headphones size={14} /> For press & partners</div>
              <h2 className="text-headline text-4xl md:text-5xl mt-3 mb-5">
                Working press? Here's the credentials.
              </h2>
              <p className="text-dek text-lg mb-6 max-w-xl">
                The full media kit — show bio, host bios, total reach across TikTok, Instagram, Facebook, Twitch, YouTube & Spotify, plus booking contacts. Download the PDF.
              </p>
              <Link to="/media-kit" className="btn-ink" data-testid="home-media-kit-cta">
                View Media Kit <ArrowRight size={16} />
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
